import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ContextualHelpLink from './ContextualHelpLink';
import './help-tour.css';

const POP_WIDTH = 280;
const VIEWPORT_MARGIN = 12;

interface HelpDotProps {
  title: string;
  body: string;
  storageKey?: string;
  /** Optional Help Center doc for "Read more" link in the popover */
  docId?: string;
}

interface PopPosition {
  top: number;
  left: number;
  placement: 'above' | 'below';
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Attach to Radix `Dialog.Content` `onPointerDownOutside` / `onFocusOutside`
 * so a portaled HelpDot popover does not dismiss the modal.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function preventDialogDismissForHelpPop(event: Event): void {
  const target = event.target;
  if (target instanceof Element && target.closest('.help-pop')) {
    event.preventDefault();
  }
}

const HelpDot: React.FC<HelpDotProps> = ({ title, body, storageKey, docId }) => {
  const { t } = useTranslation('help');
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => (storageKey ? localStorage.getItem(storageKey) === '1' : false));
  const [popPosition, setPopPosition] = useState<PopPosition | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPopPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      const popEl = popRef.current;
      if (!anchor) return;

      const popHeight = popEl?.offsetHeight ?? 180;
      const spaceAbove = anchor.top - VIEWPORT_MARGIN;
      const spaceBelow = window.innerHeight - anchor.bottom - VIEWPORT_MARGIN;
      const placement: 'above' | 'below' = spaceAbove >= popHeight + 8 || spaceAbove >= spaceBelow ? 'above' : 'below';

      let top = placement === 'above' ? anchor.top - popHeight - 8 : anchor.bottom + 8;
      top = clamp(top, VIEWPORT_MARGIN, window.innerHeight - popHeight - VIEWPORT_MARGIN);

      let left = anchor.left + anchor.width / 2 - POP_WIDTH / 2;
      left = clamp(left, VIEWPORT_MARGIN, window.innerWidth - POP_WIDTH - VIEWPORT_MARGIN);

      setPopPosition({ top, left, placement });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, title, body]);

  if (dismissed) return null;

  const handleDismiss = (checked: boolean) => {
    if (checked) {
      setDismissed(true);
      setOpen(false);
      if (storageKey) localStorage.setItem(storageKey, '1');
    }
  };

  const pop = open ? (
    <div
      ref={popRef}
      className={`help-pop help-pop--fixed${popPosition ? ` help-pop--${popPosition.placement}` : ''}`}
      style={{
        top: popPosition?.top ?? -9999,
        left: popPosition?.left ?? -9999,
        width: POP_WIDTH,
        visibility: popPosition ? 'visible' : 'hidden'
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-label={title}
    >
      <h4 className="help-pop__title">{title}</h4>
      <p className="help-pop__body">{body}</p>
      {docId && (
        <p className="help-pop__more">
          <ContextualHelpLink docId={docId} />
        </p>
      )}
      <label className="help-pop__dismiss">
        <input type="checkbox" onChange={(e) => handleDismiss(e.target.checked)} />
        {t('dontShowTipAgain')}
      </label>
    </div>
  ) : null;

  return (
    <span className="help-anchor" ref={anchorRef} data-tour="screen-help-dot">
      <button
        type="button"
        className="help-button"
        aria-label={t('helpAria', { title })}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        ?
      </button>
      {pop ? createPortal(pop, document.body) : null}
    </span>
  );
};

export default HelpDot;
